/**
 * Строка операции со свайпом влево (15.10.2026).
 *
 * Короткий свайп открывает два кружка: «Изменить» и «Удалить». Дальше
 * кружок удаления растягивается вслед за пальцем; дотянул до середины строки
 * — значок переезжает к пальцу, и отпущенная строка удаляется. Удаление сразу,
 * без вопроса: на экране — «Вернуть».
 *
 * Тап по строке по-прежнему открывает правку. Открыта одна строка за раз:
 * свайп другой строки или тап мимо закрывает прежнюю, и этот тап ничего
 * больше не делает — иначе, закрывая кнопки, человек нечаянно открывал бы
 * соседнюю операцию. Короткое или скорее вертикальное движение — прокрутка
 * списка, а не свайп.
 *
 * Всё движение — строка, растянутый кружок, значок в нём — считается в одном
 * кадре (`requestAnimationFrame`), без CSS-переходов. Прежде строку и кружок
 * доводили переходы, а значок — свой переход поверх меняющейся ширины: они
 * расходились, и на iPhone свайп подвисал, а значок дёргался.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { CategoryGlyph } from './category-icon.js';
import { Icon } from './icon.js';

/** Кружок, промежуток между кружками и отступ от края карточки. */
const D = 44;
const GAP = 10;
const PAD = 12;
/** Насколько строка уезжает, открывая оба кружка. */
export const SWIPE_OPEN = PAD + D + GAP + D + PAD;
/** За сколько точек сверх открытого кружок удаления догоняет край строки. */
const CATCH = 50;
/** Сколько пройти пальцем, прежде чем решать, свайп это или прокрутка. */
const SLOP = 8;
/** Сколько строка доезжает на место и сколько значок переезжает к пальцу, мс. */
const SETTLE_MS = 240;
const ICON_MS = 160;

const easeOut = (t: number): number => 1 - (1 - t) ** 3;
const calm = (): boolean => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export function SwipeRow({ open, onSwipe, onTap, onEdit, onDelete, label, className, children }: {
  open: boolean;
  /** Палец открыл (true) или закрыл (false) кнопки этой строки. */
  onSwipe: (open: boolean) => void;
  onTap: () => void;
  onEdit: () => void;
  onDelete: () => void;
  /** Что за операция — для диктора: «Изменить: Продукты». */
  label: string;
  /** Свой вид строки: у перевода — `tr`. */
  className?: string;
  children: ReactNode;
}) {
  const row = useRef<HTMLButtonElement>(null);
  const edit = useRef<HTMLButtonElement>(null);
  const del = useRef<HTMLButtonElement>(null);
  const icon = useRef<HTMLSpanElement>(null);
  const drag = useRef<{ x: number; y: number; id: number; base: number; side: boolean | null } | null>(null);
  /** Палец двигал строку: следующий за этим «клик» — не тап. */
  const moved = useRef(false);
  /** Строка уже уезжает с экрана: открытие и закрытие её больше не трогают. */
  const gone = useRef(false);
  /*
    Состояние движения. `x` — сдвиг строки (≤ 0); `want` — куда тянет палец,
    кадр заберёт его; `slide` — доводка до места; `arm` — где значок: 0 по
    центру кружка, 1 у пальца; `armed` — отпустить сейчас значит удалить.
  */
  const m = useRef({
    x: 0, want: null as number | null, width: 360, arm: 0, armed: false, frame: 0, last: 0,
    slide: null as { from: number; to: number; start: number; ms: number; done?: () => void } | null,
  });

  /** С какого сдвига отпущенная строка удаляется. */
  const armAt = () => Math.max(SWIPE_OPEN + 70, m.current.width * 0.6);

  /** Нарисовать текущее состояние: одна запись стилей, без чтения размеров. */
  const draw = () => {
    const r = row.current; const e = edit.current; const d = del.current; const i = icon.current;
    if (!r || !e || !d || !i) return;
    const s = m.current;
    const shown = -s.x;
    /*
      Кружок удаления растягивается влево. За первые `CATCH` точек сверх
      открытого он догоняет край строки — накрывает место «Изменить», — и
      дальше идёт вплотную за пальцем, с прежним отступом от строки.
    */
    const extra = Math.max(0, shown - SWIPE_OPEN);
    const pill = D + extra + Math.min(1, extra / CATCH) * (SWIPE_OPEN - 2 * PAD - D);
    // Кружки вырастают, пока строка их открывает.
    const grow = 0.6 + 0.4 * Math.min(1, shown / SWIPE_OPEN);
    r.style.transform = s.x ? `translate3d(${s.x}px, 0, 0)` : '';
    d.style.width = `${pill}px`;
    d.style.transform = shown < SWIPE_OPEN ? `scale(${grow})` : '';
    e.style.transform = `scale(${grow})`;
    // Растянутый кружок накрывает «Изменить» — оно гаснет.
    e.style.opacity = String(Math.max(0, 1 - extra / (CATCH * 0.6)));
    // Значок: из центра кружка к его левому краю — плавно, по `arm`.
    const k = easeOut(s.arm);
    i.style.transform = `translate3d(${-((pill - D) / 2 + k * (pill - D) / 2)}px, 0, 0)`;
  };

  const tick = (now: number) => {
    const s = m.current;
    s.frame = 0;
    const dt = Math.min(32, s.last ? now - s.last : 16);
    s.last = now;
    let again = false;
    if (s.want !== null) { s.x = s.want; s.want = null; }
    if (s.slide) {
      const a = s.slide;
      const t = a.ms <= 0 ? 1 : Math.min(1, (now - a.start) / a.ms);
      s.x = a.from + (a.to - a.from) * easeOut(t);
      if (t < 1) again = true;
      else { s.slide = null; a.done?.(); }
    }
    const on = -s.x >= armAt();
    if (on !== s.armed) {
      s.armed = on;
      del.current?.classList.toggle('armed', on);
      if (on) navigator.vibrate?.(8);
    }
    const target = s.armed ? 1 : 0;
    if (s.arm !== target) {
      const step = calm() ? 1 : dt / ICON_MS;
      s.arm = target > s.arm ? Math.min(target, s.arm + step) : Math.max(target, s.arm - step);
      if (s.arm !== target) again = true;
    }
    draw();
    if (again) s.frame = requestAnimationFrame(tick);
    else s.last = 0;
    if (!again && s.x === 0 && row.current) row.current.style.willChange = '';
  };
  const kick = () => { if (!m.current.frame) m.current.frame = requestAnimationFrame(tick); };
  /** Довести строку до `to` и, если надо, сделать что-то по приезде. */
  const slideTo = (to: number, done?: () => void) => {
    const s = m.current;
    s.want = null;
    s.slide = { from: s.x, to, start: performance.now(), ms: calm() ? 0 : SETTLE_MS, ...(done ? { done } : {}) };
    kick();
  };
  useEffect(() => { if (!gone.current) slideTo(open ? -SWIPE_OPEN : 0); }, [open]);
  useEffect(() => () => cancelAnimationFrame(m.current.frame), []);

  /** Строка уезжает влево до конца, затем удаляется. */
  const remove = () => {
    if (gone.current) return;
    gone.current = true;
    m.current.width = row.current?.offsetWidth ?? m.current.width;
    slideTo(-m.current.width, onDelete);
  };

  return (
    <div className={`ops-swipe${open ? ' open' : ''}`}>
      <div className="ops-acts">
        <button ref={edit} className="ops-act edit" tabIndex={open ? 0 : -1} aria-hidden={!open}
          aria-label={`Изменить: ${label}`} onClick={onEdit}><Icon name="pen" size={18} /></button>
        <button ref={del} className="ops-act del" tabIndex={open ? 0 : -1} aria-hidden={!open}
          aria-label={`Удалить: ${label}`} onClick={remove}>
          <span ref={icon} className="ops-act-ico"><Icon name="trash-can" size={18} /></span>
        </button>
      </div>
      <button ref={row} className={`ops-row${className ? ` ${className}` : ''}`}
        onPointerDown={(e) => {
          moved.current = false;
          if (gone.current || (e.pointerType === 'mouse' && e.button !== 0)) return;
          drag.current = { x: e.clientX, y: e.clientY, id: e.pointerId, base: 0, side: null };
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d || d.id !== e.pointerId) return;
          const dx = e.clientX - d.x;
          const dy = e.clientY - d.y;
          const s = m.current;
          if (d.side === null) {
            if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
            // Вбок сильнее, чем вниз, — свайп; иначе это прокрутка, строку не трогаем.
            if (Math.abs(dx) <= Math.abs(dy)) { drag.current = null; return; }
            d.side = true;
            moved.current = true;
            // Палец подхватывает строку там, где она сейчас, — даже на ходу.
            s.slide = null;
            d.base = s.x - dx;
            s.width = row.current?.offsetWidth ?? s.width;
            if (row.current) row.current.style.willChange = 'transform';
            try { row.current?.setPointerCapture(e.pointerId); } catch { /* палец уже отпущен */ }
          }
          // Вправо дальше закрытого не едет, влево — до края строки.
          s.want = Math.min(0, Math.max(-s.width, d.base + dx));
          kick();
        }}
        onPointerUp={() => {
          const d = drag.current;
          drag.current = null;
          if (!d?.side) return;
          const s = m.current;
          if (s.want !== null) { s.x = s.want; s.want = null; }
          if (-s.x >= armAt()) { remove(); return; }
          const next = s.x < -SWIPE_OPEN / 2;
          slideTo(next ? -SWIPE_OPEN : 0);
          if (next !== open) onSwipe(next);
        }}
        onPointerCancel={() => {
          const d = drag.current;
          drag.current = null;
          if (d?.side) slideTo(open ? -SWIPE_OPEN : 0);
        }}
        onClick={() => {
          if (moved.current) { moved.current = false; return; }
          if (gone.current) return;
          if (open) { onSwipe(false); return; }
          onTap();
        }}>
        {children}
      </button>
    </div>
  );
}

/**
 * Пример для подсказки: та же строка и те же кружки, что в списке, а палец
 * проводит по ней влево. Анимация — CSS, по кругу; строка ненастоящая и
 * нажатий не принимает.
 */
export function SwipeRowDemo() {
  return (
    <div className="swipe-demo" aria-hidden="true">
      <span className="swipe-demo-label">Пример</span>
      <div className="ops-list">
        <div className="ops-swipe">
          <div className="ops-acts">
            <span className="ops-act edit"><Icon name="pen" size={18} /></span>
            <span className="ops-act del"><span className="ops-act-ico"><Icon name="trash-can" size={18} /></span></span>
          </div>
          {/* Образец — той же строкой, что в списке (15.10.2026): счёт под суммой. */}
          <div className="ops-row">
            <CategoryGlyph icon="cart-shopping" color="green" />
            <span className="ops-text">
              <b>Продукты</b>
              <span>14:20 · Пятёрочка</span>
            </span>
            <span className="ops-right">
              <span className="ops-amt num">−1 250 ₽</span>
              <span className="ops-where">
                <span className="ops-acctag"><CategoryGlyph icon="credit-card" color="blue" size="f" /><span className="ops-accname">Основная карта</span></span>
              </span>
            </span>
          </div>
        </div>
      </div>
      <span className="swipe-demo-finger"><i className="swipe-demo-dot" /><Icon name="hand-pointer" size={40} /></span>
    </div>
  );
}
